import { inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment.dev';
import { HomesService } from '@shared/services/homes.service';
import { map, Observable, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseState } from '@shared/interfaces/base-state.interface';
import { unvalidObjectValuesClearer } from '@shared/helpers/unvalid-object-values-clearer';
import { Query } from '@shared/interfaces/query.interface';
import { PagedResponse } from './../../shared/interfaces/paged-response.interface';
import { isArray } from 'lodash';
import { CacheManager } from './cache.service';

export abstract class BaseHttpService<TItem, TNewItem> {
  protected readonly homesService = inject(HomesService);
  protected readonly cacheManager = new CacheManager();

  protected readonly http = inject(HttpClient);
  protected readonly BASE_URL = environment.baseUrl;
  protected abstract allowedMethods: {
    INSERT: boolean;
    FETCHONE: boolean;
    FETCHALL: boolean;
    UPDATE: boolean;
    DELETE: boolean;
  };
  protected abstract endpoint: string;
  public state = signal<BaseState<TItem>>({
    items: [],
    loading: false,
    ttlItems: NaN,
  });

  public insertOne(dto: TNewItem): Observable<TNewItem> {
    this.ensureMethodAllowed('INSERT');

    return this.http.post<TNewItem>(this.buildUrl(), dto);
  }

  public fetchOne(itemId: number): Observable<TItem> {
    this.ensureMethodAllowed('FETCHONE');

    const cacheKey = this.generateCacheKey('FETCHONE', itemId);

    const cached = this.cacheManager.get(cacheKey);

    if (cached) {
      if (environment.devMode)
        cached.subscribe((cachedValue) =>
          console.info('http request cached', cachedValue),
        );
      return cached as Observable<TItem>;
    }

    return this.http.get<TItem>(this.buildUrl(`/${itemId}`)).pipe(
      tap((item) => {
        this.cacheManager.set(cacheKey, item, 600000);
      }),
    );
  }

  public fetchAll(
    query?: Query,
    cacheOptions: { useCache: boolean } = { useCache: true },
  ): Observable<TItem[]> {
    this.ensureMethodAllowed('FETCHALL');

    const cacheKey = this.generateCacheKey('FETCHALL', query);

    const cached = this.cacheManager.get(cacheKey);

    if (cached && cacheOptions.useCache) {
      if (environment.devMode)
        cached.subscribe((cachedValue) =>
          console.info('http request cached', cachedValue, cacheKey),
        );
      return cached as Observable<TItem[]>;
    }

    return this.handleStateUpdate(
      this.http
        .get<TItem[]>(this.buildUrl(), { params: this.getHttpParams(query) })
        .pipe(
          tap((res) => {
            if (!isArray(res) && (res as PagedResponse<TItem>).count) {
              this.state.update((state) => ({
                ...state,
                ttlItems: (res as PagedResponse<TItem>).count,
              }));
            }
          }),
          map((res) =>
            isArray(res) ? res : (res as PagedResponse<TItem>).rows,
          ),
        ),
      'items',
    ).pipe(
      tap((items) => {
        this.cacheManager.set(cacheKey, items, 600000);
        this.state.update((state) => ({ ...state, items, loading: false }));
      }),
    );
  }

  public updateOne(itemId: number, dto: Partial<TItem>): Observable<unknown> {
    this.ensureMethodAllowed('UPDATE');

    return this.http.put<Partial<TItem>>(this.buildUrl(`/${itemId}`), dto);
  }

  public deleteOne(itemId: number): Observable<unknown> {
    this.ensureMethodAllowed('DELETE');

    return this.http.delete(this.buildUrl(`/${itemId}`));
  }

  public notifyDeveloperForNotAllowedMethod(
    method: 'INSERT' | 'FETCHONE' | 'FETCHALL' | 'UPDATE' | 'DELETE',
  ): void {
    console.error(
      `The ${method} method is not allowed for ${this.endpoint} endpoint.`,
    );
  }

  private ensureMethodAllowed(method: keyof typeof this.allowedMethods): void {
    if (!this.allowedMethods[method]) {
      this.notifyDeveloperForNotAllowedMethod(method);
      throw new Error(`Method ${method} is not allowed.`);
    }
  }

  protected buildUrl(path: string = ''): string {
    return `${this.BASE_URL}/${this.homesService.currentVisitedHome$.value.name}/${this.endpoint}${path}`;
  }

  protected getHttpParams(query?: Query): HttpParams {
    const validQuery = query ? unvalidObjectValuesClearer(query) : {};
    return new HttpParams({
      fromObject: validQuery as { [key: string]: string },
    });
  }

  private handleStateUpdate<T>(
    obs: Observable<T>,
    key: keyof BaseState<TItem>,
  ): Observable<T> {
    this.state.update((state) => ({ ...state, loading: true }));
    return obs.pipe(
      tap((data) => {
        this.state.update((state) => ({
          ...state,
          [key]: data,
          loading: false,
        }));
      }),
    );
  }

  private generateCacheKey(method: string, identifier?: unknown): string {
    const className = this.constructor.name;
    const queryPart = identifier ? `-${JSON.stringify(identifier)}` : '';
    return `${className}-${this.endpoint}-${method}${queryPart}`;
  }
}
